import { nextTick, unref } from 'vue';
import { VueRouterMeta } from '@type/vue-router-meta.types';

export function useRouterAccessibilityPattern () {

    /**
     * Update the page title as the route changes
     *
     * @param title New page title
     */
    function setRouteTitle (title: string) {
        document.title = title;
    }

    /**
     * Fix for accessibility focus issues in vue-router, namely when a
     * user of assistive technology, such as a screen reader, activates
     * a <RouterLink>, their focus stays on said link, giving them no
     * feedback at all.
     *
     * The "focusTarget" ref can specified anywhere in the component, for
     * example you may wish to set focus on the h1 after the route change.
     *
     * @param focusElement DOM element to focus on route change
     */
    function useRouterFocus (focusElement?: HTMLElement) {

        /**
         * If the url has a hash anchor, (i.e url../#content), we don't
         * want the focus to be on the top but on the actual anchor block
         */
        if (window.location.hash === '') {
            setTimeout(() => {

                /**
                 * Get the components "focusTarget" ref, if one isn't set
                 * it will use the id of "top" instead
                 */
                const focusTarget = unref(focusElement)
                    ? unref(focusElement)
                    : document.getElementById('top');

                if (!focusTarget) {
                    throw new Error('A focus element was not found.');
                }

                // Set tabindex on the ref so it can become focusable
                focusTarget.setAttribute('tabindex', '-1');

                // Set focus on the ref element
                focusTarget.focus();

                /**
                 * Remove tabindex from focusTarget
                 *
                 * @notes gov.uk discovered that a "stray" tabindex can cause
                 * issues. Therefore we remove the tabindex directly after
                 * setting focus
                 */
                focusTarget.removeAttribute('tabindex');
            }, 0);
        }
    }

    /**
     * Update the page title and set focus on route change
     *
     * @param routerMeta Vue router meta object
     * @param metaTitle Dynamic page title
     */
    function useRouterTitle (routerMeta: VueRouterMeta, metaTitle?: string) {
        nextTick(() => {

            /**
             * Update the page title, if we have a dynamic value in the Vue
             * component for `metaTitle` use that else fallback to the text set
             * in the routes
             */
            if (routerMeta.title) {
                setRouteTitle(
                    metaTitle
                        ? metaTitle
                        : routerMeta.title
                );
            }
        });
    }

    return {
        useRouterFocus,
        useRouterTitle
    };
}
